import { AccessMemberGroup, ArticleFlat, MinimalChannel, SettingsMember } from 'types'
import { useCallback, useEffect, useState } from 'react'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import { useWorkspaceChannels } from 'hooks/useWorkspaceChannels'
import { useIsNotSelf } from 'hooks/useIsNotSelf'
import { ArticleClient } from 'api/clients/ArticleClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ourUuid } from '../../../utils'

interface Props {
  article?: ArticleFlat
}

export const useSourceData = ({ article }: Props) => {
  const [workspaceGroup, setWorkspaceGroup] = useState<AccessMemberGroup>()
  const [accessGroup, setAccessGroup] = useState<AccessMemberGroup>()
  const currentWorkspace = useCurrentWorkspace()
  const currentWorkspaceId = currentWorkspace?.key
  const workspaceMembers = useWorkspaceMembers(currentWorkspaceId, { includeInactive: false })
  const workspaceChannels = useWorkspaceChannels(currentWorkspaceId, true)
  const isNotSelf = useIsNotSelf()

  const createAccessGroup = useCallback((permissions: any): AccessMemberGroup | null => {
    const { channels, users } = permissions
    const checkPermission = /^(edit|comment)$/
    const accessMembers: SettingsMember[] = []
    const accessChannels: MinimalChannel[] = []

    if (users.length) {
      users?.forEach((user: any) => {
        if (user?.direct_permission?.permission.match(checkPermission)) {
          accessMembers.push(user)
        }
      })
    }

    if (channels.length) {
      channels?.forEach((channel: any) => {
        if (channel?.direct_permission?.permission.match(checkPermission)) {
          accessChannels.push(channel)
        }
      })
    }

    if (accessMembers.length || accessChannels.length) {
      const accessGroup = {
        id: ourUuid(),
        label: 'Everyone',
        name: 'Users with comment or edit access to this doc',
        members: accessMembers,
        channels: accessChannels,
      }
      setAccessGroup(accessGroup)
      return accessGroup
    }
    return null
  }, [])

  useEffect(() => {
    if (!article) {
      return
    }
    const members = workspaceMembers.filter((member) => {
      return article?.creator_id !== member.id && isNotSelf(member)
    })
    const workspaceAccessGroup = {
      id: ourUuid(),
      label: 'Everyone-workspace',
      logo_url: currentWorkspace?.logo_url,
      name: 'Everyone in this workspace',
      members,
      channels: [],
    }
    if (members.length) {
      setWorkspaceGroup(workspaceAccessGroup)
    }
  }, [article, currentWorkspace, workspaceMembers, isNotSelf])

  /*
   * Load access data
   */
  useEffect(() => {
    if (!article) {
      return
    }
    const loadAccessData = async () => {
      if (!article?.id) {
        return
      }

      let res = await ArticleClient.getAccessesForShare(article?.id)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        if (res?.data?.permissions) {
          createAccessGroup(res.data.permissions)
        }
      }
    }
    loadAccessData()
  }, [article, workspaceMembers, createAccessGroup]) // eslint-disable-line react-hooks/exhaustive-deps

  return (includeGroups: boolean = true, includeSelf: boolean = false) => ({
    users: includeSelf ? workspaceMembers : workspaceMembers.filter(isNotSelf),
    channels: includeGroups ? workspaceChannels : [],
    accessMemberGroups: includeGroups ? ([workspaceGroup, accessGroup].filter(Boolean) as AccessMemberGroup[]) : [],
  })
}
